/**
 * The simulation (spec 06 §4): `createWorld` sets a level up, `step` runs one 50 Hz frame.
 *
 * Order within a frame, as the C64 runs it (02 §1.1): the clocks decide what ticks; on an I-tick
 * the players, then the monsters and the Baron, move, then the items; on an M-tick (a main-loop pass)
 * the bubbles move, trap, pop and chain, then the monsters' shots, the Baron and the power-up
 * effects; then the level flow sees the result. While the flow is frozen (the drop-in, the HURRY UP
 * banner) only the monsters' drop-in runs; while the players fly in after a scroll, nothing else does.
 */

import { updateBubbles, updateItems } from './bubbles';
import { stepClock, initialClock } from './clock';
import { flyInFrame, initialFlow, updateFlow } from './flow';
import { itemsITick, itemsMTick, setupItems } from './items';
import { spawnMonsters, updateBarons, updateMonsters, updateProjectiles } from './monsters';
import { playfieldOf } from './playfield';
import { placeAtStart, updatePlayers } from './players';
import { initialMotion } from './players/state';
import { saveRng } from './random';
import { secretTick } from './secret';
import { Pcg32 } from './rng';
import { START_LIVES } from './tuning';
import type { LevelDef } from '../levels/types';
import type { CarryState, Command, Player, PlayerId, PowerState, SimOptions, World } from './types';

export const IDLE: Command = { left: false, right: false, jump: false, fire: false };

export interface WorldSetup {
  /** Which players are in the game from the start. */
  players: readonly [boolean, boolean];
  seed: number;
  options?: Partial<SimOptions>;
  /** What the last level handed on; a new game starts afresh. */
  carry?: CarryState;
}

function newPlayer(id: PlayerId, playing: boolean): Player {
  return {
    id, state: playing ? 'play' : 'out', x: 0, y: 0, facing: id === 0 ? 1 : -1, lives: START_LIVES, score: 0,
    extend: [false, false, false, false, false, false], invulnerable: 0, nextExtraLife: 0, motion: initialMotion(),
  };
}

/** No power-up effects: every round starts so (02 §7.5: rings, crosses and the heart last a round). */
export function initialPower(): PowerState {
  return { clock: 0, heart: -1, redCross: [0, 0], lightning: [false, false], giant: -1 };
}

/** A new game's carry: food rolled afresh, the door counter at 18, the bubble-food schedule from level 1. */
export function initialCarry(players: readonly [boolean, boolean]): CarryState {
  return { foodBase: -1, doorAt: 18, unharmed: [players[0], players[1]], bubbleFoodAt: 1, bubbleFoodGap: 4 };
}

export function createWorld(level: LevelDef, setup: WorldSetup): World {
  const world: World = {
    level,
    levelIndex: level.n - 1,
    playfield: playfieldOf(level),
    options: { c64Slowdown: false, c64Continue: false, suporr: false, ...setup.options },
    clock: initialClock(),
    rng: new Pcg32(setup.seed, 0x62b).getState(),
    players: [newPlayer(0, setup.players[0]), newPlayer(1, setup.players[1])],
    bubbles: [],
    monsters: [],
    barons: [],
    items: [],
    projectiles: [],
    flow: initialFlow(level.n),
    continues: 10 - setup.players.filter(Boolean).length,
    events: [],
    nextId: 1,
    boss: null,
    power: initialPower(),
    carry: setup.carry ? { ...setup.carry, unharmed: [...setup.carry.unharmed] } : initialCarry(setup.players),
  };
  placeAtStart(world, 0);
  placeAtStart(world, 1);
  spawnMonsters(world);
  // The secret room (level 0) has no timed items (02 §9.3); the session sets its diamonds up.
  if (level.n > 0) setupItems(world);
  return world;
}

/**
 * Carry the players into the next level (their scores, lives, letters, shoe and candies - which last
 * until death, 02 §7.5; rings end with the round): a new world for `level` whose players are those
 * of `from`. They fly in from where they were to their starts (02 §9.1) before anything moves.
 */
export function nextLevelWorld(from: World, level: LevelDef, seed: number): World {
  const world = createWorld(level, { players: [false, false], seed, options: from.options, carry: from.carry });
  const carry = (p: Player): Player => JSON.parse(JSON.stringify(p)) as Player;
  world.players = [carry(from.players[0]), carry(from.players[1])];
  world.continues = from.continues;
  for (const p of world.players) {
    const was = p.motion;
    p.motion = initialMotion();
    p.motion.shoe = was.shoe;
    p.motion.yellowCandy = was.yellowCandy;
    p.motion.purpleCandy = was.purpleCandy;
    p.motion.blueCandy = was.blueCandy;
    p.invulnerable = 0;
  }
  const f = world.flow;
  for (const id of [0, 1] as const) {
    f.flyFrom[id] = { x: from.players[id].x, y: from.players[id].y };
    placeAtStart(world, id);
    f.flyTo[id] = { x: world.players[id].x, y: world.players[id].y };
  }
  f.phase = 'flyIn';
  f.freeze = 0;
  flyInFrame(world, 0);
  return world;
}

/** Software sprites the C64's main loop would be drawing (for the C64 slowdown option). */
const softwareEntities = (w: World): number => w.bubbles.length + w.items.length + w.projectiles.length + w.barons.length;

/** Run one frame. `commands` are this frame's controls for player 1 and player 2. */
export function step(world: World, commands: readonly [Command, Command]): void {
  world.events.length = 0;
  const c = world.clock;
  stepClock(c, world.options.c64Slowdown, softwareEntities(world));
  if (world.flow.phase !== 'flyIn') {
    const frozen = world.flow.freeze > 0;
    if (c.iTick) {
      if (!frozen) updatePlayers(world, commands);
      updateMonsters(world);
      if (!frozen) {
        updateItems(world);
        itemsITick(world);
        secretTick(world);
      }
    }
    if (c.mTick && !frozen) {
      // Bursts, the players' push (D_0AAB) and the bubbles' own pass, in the C64's order.
      updateBubbles(world);
      updateProjectiles(world);
      // The Baron moves on main-loop passes (its handler at $1473).
      updateBarons(world);
      itemsMTick(world);
    }
  }
  updateFlow(world);
  saveRng(world);
}
